import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import { sortBy } from 'lib/array/arrayUtils'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { MaldivesDirectBundleProvider } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import Group from 'components/utils/Group'
import { FlightsClassTypes, RewardFareClass } from 'constants/flight'
import { CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import useMaldivesCharterRecommendedOfferList from 'hooks/MaldivesBundle/useMaldivesCharterRecommendedOfferList'
import useOffers from 'hooks/Offers/useOffers'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import styled from 'styled-components'
import noop from 'lib/function/noop'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'

const CarouselContainer = styled(Pane)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

interface Props {
  titleVariant?: React.ComponentProps<typeof Heading>['variant']
  overrideTitle?: React.ReactNode
  overrideOfferList?: App.OfferList
  excludedIds?: Array<string>
  useRewardPreviewPricing?: boolean
  /** When false, keep the carousel shell visible even if the offer list resolves empty. */
  hideWhenEmpty?: boolean
  /** When false, skip the streamed bundle-pricing fetch (see useMaldivesCharterRecommendedOfferList). */
  includeBundleStream?: boolean
  /** Direction for the client-side price sort applied after offer details load. */
  sortDirection?: 'asc' | 'desc'
}

/**
 * Displays Maldives charter flight offers
 */
function MaldivesCharterCarousel({
  titleVariant = 'heading2',
  overrideTitle,
  overrideOfferList,
  excludedIds,
  useRewardPreviewPricing = false,
  hideWhenEmpty = true,
  includeBundleStream = true,
  sortDirection = 'asc',
}: Props) {
  const intl = useIntl()
  const maldivesOfferList = useMaldivesCharterRecommendedOfferList({ excludedIds, includeBundleStream })
  const title = overrideTitle ?? intl.formatMessage(
    {
      id: 'jp0DeXBun5quDP3QTHw_',
      defaultMessage: 'Australia’s <i>only</i> non-stop flights to the Maldives',
      description: 'Title for Maldives charter carousel with italicized only',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  // Link to Maldives Direct charter page
  const searchUrl = `/campaign/${CHARTER_PAGE_SLUG}`

  // Bundle context for displaying flight + hotel prices
  const bundleContextValue = useMemo(() => ({
    charterBundle: true,
    isPricePerPerson: true,
    fareClass: FlightsClassTypes.ECONOMY,
    selectedFareClass: useRewardPreviewPricing ? RewardFareClass.ECONOMY_REWARDS : undefined,
    allowedFareClassesByOfferId: new Map(),
    hasFareClassRestrictionsByOfferId: new Map(),
    canOpenFareClassModal: false,
    openFareClassModal: noop,
    hasUserSelectedDates: false,
    useRewardPreviewPricing,
  }), [useRewardPreviewPricing])

  // Fetch full offer objects for price sorting
  const [offers, offersFetching] = useOffers<App.HotelOffer>(maldivesOfferList.offerIds)

  const allOffersLoaded = !maldivesOfferList.fetching &&
    !offersFetching &&
    offers.length > 0 &&
    offers.length === maldivesOfferList.offerIds.length

  // TODO: Revisit Maldives Rewards sorting so reward previews can rank by the relevant bundle/reward value, not just base hotel price.
  // Sort offers by price (cheapest first) - client-side sorting since light endpoint doesn't support sortBy
  const sortedOfferList = useMemo<App.OfferList>(() => {
    if (!allOffersLoaded) {
      return maldivesOfferList
    }

    const priceById = new Map(
      offers.map(offer => [offer.id, offer.lowestPricePackage?.price ?? Number.MAX_VALUE]),
    )

    const sortedOfferIds = sortBy(
      maldivesOfferList.offerIds,
      id => priceById.get(id) ?? Number.MAX_VALUE,
      sortDirection,
    )

    return { ...maldivesOfferList, offerIds: sortedOfferIds }
  }, [maldivesOfferList, allOffersLoaded, offers, sortDirection])

  if (
    hideWhenEmpty &&
    !maldivesOfferList.fetching &&
    maldivesOfferList.offerIds.length === 0
  ) {
    return null
  }

  return (
    <CarouselContainer>
      <VerticalSpacer gap={24}>
        <LayoutContainer>
          <VerticalSpacer gap={16}>
            <Group
              direction="horizontal"
              horizontalAlign="space-between"
              verticalAlign="center"
              gap={16}
            >
              <Group direction="horizontal" gap={16} verticalAlign="center">
                <Heading variant={titleVariant}>{title}</Heading>
              </Group>
              <Group direction="horizontal" gap={16} verticalAlign="center">
                <TextButton
                  kind="tertiary"
                  to={searchUrl}
                >
                  {intl.formatMessage({
                    id: 'vwEHJ5zO8eo5nweHB3ZI',
                    defaultMessage: 'View all',
                    description: 'Hyperlink text to view all offers in the hero carousel',
                  })}
                </TextButton>
              </Group>
            </Group>
          </VerticalSpacer>
        </LayoutContainer>
        <MaldivesDirectBundleProvider value={bundleContextValue}>
          <OfferListCarousel
            overrideOfferList={overrideOfferList ?? sortedOfferList}
            selectedDatesAndRoomsFilter={maldivesOfferList.bundleFilters}
            additionalOfferUrlQuery={useRewardPreviewPricing ? 'fareClass=economy-rewards' : undefined}
            tabletLimit="default"
            tileStyle="card-default"
            tileSize="medium"
          >
            <OfferCarouselLookingForSomethingElse to={searchUrl} />
          </OfferListCarousel>
        </MaldivesDirectBundleProvider>
      </VerticalSpacer>
    </CarouselContainer>
  )
}
export default MaldivesCharterCarousel
